import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { environment } from 'src/environments/environment';
import { JournalOptionEnum } from '~modules/connectors/constants/journal.options';
import { defaultAccountLabelMaxLength, defaultAuxiliaryAccountMaxLength, defaultJournalCodeMaxLength, defaultMainAccountMaxLength, defaultPaymentMethodNameMaxLength, PaymentMethodsSectionFieldsConfiguration } from '~modules/connectors/services/extract-connectors-configurations/field-type';
import { PaymentMethodConfiguration } from '../../entities/payment-method-configuration.entity';

import { debounceTime, ReplaySubject, Subscription } from 'rxjs';
import { ConfigurationModeEnum } from '~modules/connectors/modules/load/common/services/configuration-mode.service';

export type PaymentMethodConfigurationControls = FormGroup<{
  id: FormControl<number | null>;
  name: FormControl<string | null>;
  export: FormControl<boolean | null>;
  journalCode: FormControl<string | null>;
  internalTransferAccount: FormControl<string | null>;
  internalTransferAccountLabel: FormControl<string | null>;
  providerAccount: FormControl<string | null>;
  providerAccountLabel: FormControl<string | null>;
  bankSupplierAccount: FormControl<string | null>;
  bankSupplierAccountLabel: FormControl<string | null>;
  bankSupplierAuxiliaryAccount: FormControl<string | null>;
  bankSupplierAuxiliaryAccountLabel: FormControl<string | null>;
}>;

@Component({
  selector: 'app-payment-method-configuration',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,

    MatFormFieldModule,
    MatIconModule,
    MatSelectModule,
    MatInputModule,
    MatSlideToggleModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatTooltipModule

  ],
  providers: [
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        subscriptSizing: 'dynamic'
      }
    }
  ],
  templateUrl: './payment-method-configuration.component.html',
  styleUrls: ['./payment-method-configuration.component.scss']
})
export class PaymentMethodConfigurationComponent implements OnInit, OnDestroy {

  formGroup!: FormGroup;
  formArray!: FormArray<PaymentMethodConfigurationControls>;

  internalTransferSubscriptions: Subscription[] = [];
  formArraySubscription?: Subscription;
  filteredOptions$ = new ReplaySubject<string[]>(1);
  paymentOptions!: string[];
  configurationMode: keyof typeof ConfigurationModeEnum | undefined;
  fetchedFieldsConfiguration: PaymentMethodConfiguration[] | undefined;
  fieldsConfig: PaymentMethodsSectionFieldsConfiguration | undefined;
  journalOptionValue: JournalOptionEnum | undefined = undefined;
  currentMethods: PaymentMethodConfiguration[] | undefined;
  useSuggestedAccountToggle!: boolean;

  @Input()
  set journalOption(value: JournalOptionEnum | undefined) {
    this.journalOptionValue = value;
    if (value) {
      if (value === JournalOptionEnum.UNIQUE) {
        this.formArray.controls.forEach(methodConfiguration => {
          methodConfiguration.get('journalCode')?.setValidators([]);
          methodConfiguration.get('journalCode')?.setValue(null);
        });
      } else {
        this.formArray.controls.forEach(methodConfiguration => {
          methodConfiguration.get('journalCode')?.setValidators([Validators.required]);
          const actualValue: string | undefined | null = methodConfiguration.get('journalCode')?.value;
          methodConfiguration.get('journalCode')?.setValue(actualValue || this.fieldsConfig?.columns?.journalCode?.defaultValue || '');
        });
      }
    }
  }

  get isJournalVisible() {
    return this.journalOptionValue == undefined || this.journalOptionValue === JournalOptionEnum.BY_PAYMENT_METHOD;
  }

  @Input()
  toolLabel!: string | undefined;

  @Input()
  addLabel!: string | undefined;
  @Input()
  headerAccountLabel!: string | undefined;
  @Input()
  inputProviderAccountLabel!: string | undefined;
  @Input()
  defaultTooltipLabel!: string | undefined;

  @Input()
  set useSuggestedAccount(value: boolean) {
    this.useSuggestedAccountToggle = value;
    if (this.formArray) {
      if (value === true) {
        const lastIndex = this.formArray.length - 1;

        this.formArray.controls.forEach((group, index) => {
          if (index !== lastIndex) {
            group.get('name')?.disable({ emitEvent: false });
            group.get('journalCode')?.disable({ emitEvent: false });
            group.get('providerAccount')?.disable({ emitEvent: false });
          }
        });
      } else {
        this.formArray.controls.forEach((group) => {
        group.get('name')?.enable({ emitEvent: false });
        group.get('journalCode')?.enable({ emitEvent: false });
        group.get('providerAccount')?.enable({ emitEvent: false });
        });
      }
    }
  }

  @Input()
  set fetchedFieldsConfigurationInput(fetchedFieldsConfigurationInput: PaymentMethodConfiguration[] | undefined) {
    if (fetchedFieldsConfigurationInput && fetchedFieldsConfigurationInput.length > 0) {
      this.fetchedFieldsConfiguration = fetchedFieldsConfigurationInput;
      const paymentMethodOptions = fetchedFieldsConfigurationInput.map((paymentMethod) => paymentMethod.name)
      this.filteredOptions$.next(paymentMethodOptions);
      this.paymentOptions = paymentMethodOptions;
      // if no methods are given (new sections like new connector or bank added to existing connector or payment added to existing connector), we add fetchedFieldsConfiguration to fieldsConfiguration.
      if (!this.currentMethods) {
        this.applyFetchedFieldsConfiguration();
      }
    }
  }

  get supportEmail() {
    return environment.supportEmail;
  }

  @Input()
  set configMode(configMode: keyof typeof ConfigurationModeEnum | undefined) {
    this.configurationMode = configMode;
  }

  @Input()
  set methods(value: PaymentMethodConfiguration[] | undefined) {
    if (value) {
      this.currentMethods = value;
      if (this.formArray.controls.length !== 0) {
        this.formArray.clear()
      }
      let defaultMethod = null;
      for(const methodConfiguration of value) {
        if (methodConfiguration.name == null) {
          if (defaultMethod != null) {
            console.error('There are two or more default methods');
          }
          defaultMethod = methodConfiguration;
        } else {
          this.formArray.push(
            this.createPaymentMethodConfigurationGroup(
              methodConfiguration.id,
              methodConfiguration.name,
              methodConfiguration.export,
              methodConfiguration.journalCode,
              methodConfiguration.internalTransferAccount,
              methodConfiguration.internalTransferAccountLabel,
              methodConfiguration.providerAccount,
              methodConfiguration.providerAccountLabel,
              methodConfiguration.bankSupplierAccount,
              methodConfiguration.bankSupplierAccountLabel,
              methodConfiguration.bankSupplierAuxiliaryAccount,
              methodConfiguration.bankSupplierAuxiliaryAccountLabel
            )
          );
        }
      }

      if (defaultMethod != null) {
        this.formArray.push(
          this.createPaymentMethodConfigurationGroup(
           defaultMethod.id,
           defaultMethod.name,
           defaultMethod.export,
           defaultMethod.journalCode,
           defaultMethod.internalTransferAccount,
           defaultMethod.internalTransferAccountLabel,
           defaultMethod.providerAccount,
           defaultMethod.providerAccountLabel,
           defaultMethod.bankSupplierAccount,
           defaultMethod.bankSupplierAccountLabel,
           defaultMethod.bankSupplierAuxiliaryAccount,
           defaultMethod.bankSupplierAuxiliaryAccountLabel,
           true
          )
        );
      } else {
        console.error('There is no default methods');
      }
    }
  }

  @Input()
  set fieldsConfiguration (value: PaymentMethodsSectionFieldsConfiguration | undefined) {
    if (value != undefined) {
      this.fieldsConfig = value;
      this.paymentOptions = this.fieldsConfig?.paymentMethodOptions ?? [];
      this.filteredOptions$.next(this.paymentOptions);
      if (this.formArray.controls.length === 0) {
        for(const methodConfiguration of value.methods || []) {
          this.formArray.push(
            this.createPaymentMethodConfigurationGroup(
              null,
              methodConfiguration.name,
              methodConfiguration.export,
              methodConfiguration.journalCode,
              methodConfiguration.internalTransfer?.account,
              methodConfiguration.internalTransfer?.label,
              methodConfiguration.provider?.account,
              methodConfiguration.provider?.label,
              methodConfiguration.bankSupplier?.account,
              methodConfiguration.bankSupplier?.label,
              methodConfiguration.bankSupplier?.auxiliaryAccount,
              methodConfiguration.bankSupplier?.auxiliaryLabel
            )
          );
        }

        this.formArray.push(
          this.createPaymentMethodConfigurationGroup(
            null,
            value.defaultMethod.name,
            value.defaultMethod.export,
            value.defaultMethod.journalCode,
            value.defaultMethod.internalTransfer?.account,
            value.defaultMethod.internalTransfer?.label,
            value.defaultMethod.provider?.account,
            value.defaultMethod.provider?.label,
            value.defaultMethod.bankSupplier?.account,
            value.defaultMethod.bankSupplier?.label,
            value.defaultMethod.bankSupplier?.auxiliaryAccount,
            value.defaultMethod.bankSupplier?.auxiliaryLabel,
            true
          )
        );
      } else {
        const nameMaxLength = this.fieldsConfig?.columns?.name?.maxLength != null ? this.fieldsConfig?.columns?.name?.maxLength : defaultPaymentMethodNameMaxLength;
        const journalCodeAccountMaxLength = this.fieldsConfig?.columns?.journalCode?.maxLength != null ? this.fieldsConfig?.columns?.journalCode?.maxLength : defaultJournalCodeMaxLength;
        const internalTransferAccountMaxLength = this.fieldsConfig?.columns?.internalTransfer?.account?.maxLength != null ? this.fieldsConfig?.columns?.internalTransfer?.account?.maxLength : defaultMainAccountMaxLength;
        const internalTransferAccountLabelMaxLength = this.fieldsConfig?.columns?.internalTransfer?.label?.maxLength != null ? this.fieldsConfig?.columns?.internalTransfer?.label?.maxLength : defaultAccountLabelMaxLength;
        const providerAccountMaxLength = this.fieldsConfig?.columns?.provider?.account?.maxLength != null ? this.fieldsConfig?.columns?.provider?.account?.maxLength : defaultMainAccountMaxLength;
        const providerAccountLabelMaxLength = this.fieldsConfig?.columns?.provider?.label?.maxLength != null ? this.fieldsConfig?.columns?.provider?.label?.maxLength : defaultAccountLabelMaxLength;
        const bankSupplierAccountMaxLength = this.fieldsConfig?.columns?.bankSupplier?.account?.maxLength != null ? this.fieldsConfig?.columns?.bankSupplier?.account?.maxLength : defaultMainAccountMaxLength;
        const bankSupplierAccountLabelMaxLength = this.fieldsConfig?.columns?.bankSupplier?.label?.maxLength != null ? this.fieldsConfig?.columns?.bankSupplier?.label?.maxLength : defaultAccountLabelMaxLength;
        const bankSupplierAuxiliaryAccountMaxLength = this.fieldsConfig?.columns?.bankSupplier?.auxiliaryAccount?.maxLength != null ? this.fieldsConfig?.columns?.bankSupplier?.auxiliaryAccount?.maxLength : defaultAuxiliaryAccountMaxLength;
        const bankSupplierAuxiliaryAccountLabelMaxLength = this.fieldsConfig?.columns?.bankSupplier?.auxiliaryLabel?.maxLength != null ? this.fieldsConfig?.columns?.bankSupplier?.auxiliaryLabel?.maxLength : defaultAccountLabelMaxLength;

        this.formArray.controls.forEach((methodConfiguration, index) => {
          // name
          methodConfiguration.get('name')?.setValidators([Validators.maxLength(nameMaxLength)]);
          if ((value.columns?.name?.canEdit != null && !value.columns?.name?.canEdit) || index === this.formArray.controls.length - 1) {
            methodConfiguration.get('name')?.disable();
          } else {
            methodConfiguration.get('name')?.enable();
          }
          // export
          if ((value.columns?.export?.canEdit != null && !value.columns?.export?.canEdit) || index === this.formArray.controls.length - 1) {
            methodConfiguration.get('export')?.disable();
          } else {
            methodConfiguration.get('export')?.enable();
          }
          // journalCode
          const journalCodeValidators: ValidatorFn[] = [Validators.maxLength(journalCodeAccountMaxLength)];
          if (this.journalOptionValue == undefined || this.journalOptionValue === JournalOptionEnum.BY_PAYMENT_METHOD) {
            journalCodeValidators.push(Validators.required);
          }
          methodConfiguration.get('journalCode')?.setValidators(journalCodeValidators);
          if ((value.columns?.journalCode?.canEdit != null && !value.columns?.journalCode?.canEdit)) {
            methodConfiguration.get('journalCode')?.disable();
          } else {
            methodConfiguration.get('journalCode')?.enable();
          }
          // internal transfer
          methodConfiguration.get('internalTransferAccount')?.setValidators([Validators.maxLength(internalTransferAccountMaxLength)]);
          methodConfiguration.get('internalTransferAccountLabel')?.setValidators([Validators.maxLength(internalTransferAccountLabelMaxLength)]);
          if ((value.columns?.internalTransfer?.canEdit != null && !value.columns?.internalTransfer?.canEdit)) {
            methodConfiguration.get('internalTransferAccount')?.disable();
            methodConfiguration.get('internalTransferAccountLabel')?.disable();
          } else {
            methodConfiguration.get('internalTransferAccount')?.enable();
            methodConfiguration.get('internalTransferAccountLabel')?.enable();
          }
          // provider
          methodConfiguration.get('providerAccount')?.setValidators([Validators.maxLength(providerAccountMaxLength)]);
          methodConfiguration.get('providerAccountLabel')?.setValidators([Validators.maxLength(providerAccountLabelMaxLength)]);
          if ((value.columns?.provider?.canEdit != null && !value.columns?.provider?.canEdit)) {
            methodConfiguration.get('providerAccount')?.disable();
            methodConfiguration.get('providerAccountLabel')?.disable();
          } else {
            methodConfiguration.get('providerAccount')?.enable();
            methodConfiguration.get('providerAccountLabel')?.enable();
          }
          // bank supplier
          methodConfiguration.get('bankSupplierAccount')?.setValidators([Validators.maxLength(bankSupplierAccountMaxLength)]);
          methodConfiguration.get('bankSupplierAccountLabel')?.setValidators([Validators.maxLength(bankSupplierAccountLabelMaxLength)]);
          methodConfiguration.get('bankSupplierAuxiliaryAccount')?.setValidators([Validators.maxLength(bankSupplierAuxiliaryAccountMaxLength)]);
          methodConfiguration.get('bankSupplierAuxiliaryAccountLabel')?.setValidators([Validators.maxLength(bankSupplierAuxiliaryAccountLabelMaxLength)]);
          if ((value.columns?.bankSupplier?.canEdit != null && !value.columns?.bankSupplier?.canEdit)) {
            methodConfiguration.get('bankSupplierAccount')?.disable();
            methodConfiguration.get('bankSupplierAccountLabel')?.disable();
            methodConfiguration.get('bankSupplierAuxiliaryAccount')?.disable();
            methodConfiguration.get('bankSupplierAuxiliaryAccountLabel')?.disable();
          } else {
            methodConfiguration.get('bankSupplierAccount')?.enable();
            methodConfiguration.get('bankSupplierAccountLabel')?.enable();
            methodConfiguration.get('bankSupplierAuxiliaryAccount')?.disable();
            methodConfiguration.get('bankSupplierAuxiliaryAccountLabel')?.disable();
          }
        })
      }

      this.formGroup.markAllAsTouched();
      // update labels in case of max length update
      this.updateLabelsForMethods();
    }
  }

  constructor(
    private readonly fb: FormBuilder,
  ) {
    this.formArray = new FormArray<PaymentMethodConfigurationControls>([], [Validators.required]);
    this.formGroup = new FormGroup({
      formArray: this.formArray
    });

  }
  ngOnInit(): void {
    // set labels
    this.updateLabelsForMethods();
    // update labels subscription
    this.formArraySubscription = this.formArray.valueChanges.pipe(debounceTime(200)).subscribe(() => {
      this.updateLabelsForMethods();
    });
  }

  ngOnDestroy(): void {
    this.internalTransferSubscriptions.forEach((subscription) => subscription.unsubscribe());
    this.formArraySubscription?.unsubscribe();
  }

  addPaymentMethod() {
    const paymentMethodConfigurationGroup: PaymentMethodConfigurationControls = this.createPaymentMethodConfigurationGroup(
      null,
      this.fieldsConfig?.columns?.name?.defaultValue || null,
      this.fieldsConfig?.columns?.export?.defaultValue || null,
      this.fieldsConfig?.columns?.journalCode?.defaultValue || null,
      this.fieldsConfig?.columns?.internalTransfer?.account?.defaultValue || null,
      this.fieldsConfig?.columns?.internalTransfer?.label?.defaultValue || null,
      this.fieldsConfig?.columns?.provider?.account?.defaultValue || null,
      this.fieldsConfig?.columns?.provider?.label?.defaultValue || null,
      this.fieldsConfig?.columns?.bankSupplier?.account?.defaultValue || null,
      this.fieldsConfig?.columns?.bankSupplier?.label?.defaultValue || null,
      this.fieldsConfig?.columns?.bankSupplier?.auxiliaryAccount?.defaultValue || null,
      this.fieldsConfig?.columns?.bankSupplier?.auxiliaryLabel?.defaultValue || null
    );
    this.formArray.insert(this.formArray.controls.length - 1, paymentMethodConfigurationGroup);
  }

  deletePaymentMethod(index: number) {
    this.internalTransferSubscriptions.at(index)?.unsubscribe();
    delete this.internalTransferSubscriptions[index];
    this.formArray.removeAt(index);
  }

  createPaymentMethodConfigurationGroup(
    id: number | null = null,
    name: string | null = null,
    exportValue: boolean | null = null,
    journalCode: string | null = null,
    internalTransferAccount: string | null = null,
    internalTransferAccountLabel: string | null = null,
    providerAccount: string | null = null,
    providerAccountLabel: string | null = null,
    bankSupplierAccount: string | null = null,
    bankSupplierAccountLabel: string | null = null,
    bankSupplierAuxiliaryAccount: string | null = null,
    bankSupplierAuxiliaryAccountLabel: string | null = null,
    isDefault = false
  ): PaymentMethodConfigurationControls {
    const nameMaxLength = this.fieldsConfig?.columns?.name?.maxLength != null ? this.fieldsConfig?.columns?.name?.maxLength : defaultPaymentMethodNameMaxLength;
    const journalCodeAccountMaxLength = this.fieldsConfig?.columns?.journalCode?.maxLength != null ? this.fieldsConfig?.columns?.journalCode?.maxLength : defaultJournalCodeMaxLength;
    const internalTransferAccountMaxLength = this.fieldsConfig?.columns?.internalTransfer?.account?.maxLength != null ? this.fieldsConfig?.columns?.internalTransfer?.account?.maxLength : defaultMainAccountMaxLength;
    const internalTransferAccountLabelMaxLength = this.fieldsConfig?.columns?.internalTransfer?.label?.maxLength != null ? this.fieldsConfig?.columns?.internalTransfer?.label?.maxLength : defaultAccountLabelMaxLength;
    const providerAccountMaxLength = this.fieldsConfig?.columns?.provider?.account?.maxLength != null ? this.fieldsConfig?.columns?.provider?.account?.maxLength : defaultMainAccountMaxLength;
    const providerAccountLabelMaxLength = this.fieldsConfig?.columns?.provider?.label?.maxLength != null ? this.fieldsConfig?.columns?.provider?.label?.maxLength : defaultAccountLabelMaxLength;
    const bankSupplierAccountMaxLength = this.fieldsConfig?.columns?.bankSupplier?.account?.maxLength != null ? this.fieldsConfig?.columns?.bankSupplier?.account?.maxLength : defaultMainAccountMaxLength;
    const bankSupplierAccountLabelMaxLength = this.fieldsConfig?.columns?.bankSupplier?.label?.maxLength != null ? this.fieldsConfig?.columns?.bankSupplier?.label?.maxLength : defaultAccountLabelMaxLength;
    const bankSupplierAuxiliaryAccountMaxLength = this.fieldsConfig?.columns?.bankSupplier?.auxiliaryAccount?.maxLength != null ? this.fieldsConfig?.columns?.bankSupplier?.auxiliaryAccount?.maxLength : defaultAuxiliaryAccountMaxLength;
    const bankSupplierAuxiliaryAccountLabelMaxLength = this.fieldsConfig?.columns?.bankSupplier?.auxiliaryLabel?.maxLength != null ? this.fieldsConfig?.columns?.bankSupplier?.auxiliaryLabel?.maxLength : defaultAccountLabelMaxLength;

    const journalCodeValidators: ValidatorFn[] = [Validators.maxLength(journalCodeAccountMaxLength)];
    if (this.journalOptionValue == undefined || this.journalOptionValue === JournalOptionEnum.BY_PAYMENT_METHOD) {
      journalCodeValidators.push(Validators.required);
    }
    const result: PaymentMethodConfigurationControls = new FormGroup({
      id: new FormControl<number | null>(id),
      name: new FormControl<string | null>(name, [Validators.maxLength(nameMaxLength)]),
      export: new FormControl<boolean | null>(exportValue),
      journalCode: new FormControl<string | null>(journalCode, journalCodeValidators),
      internalTransferAccount: new FormControl<string | null>(internalTransferAccount, [Validators.maxLength(internalTransferAccountMaxLength)]),
      internalTransferAccountLabel: new FormControl<string | null>(internalTransferAccountLabel, [Validators.maxLength(internalTransferAccountLabelMaxLength)]),
      providerAccount: new FormControl<string | null>(providerAccount, [Validators.required, Validators.maxLength(providerAccountMaxLength)]),
      providerAccountLabel: new FormControl<string | null>(providerAccountLabel, [Validators.maxLength(providerAccountLabelMaxLength)]),
      bankSupplierAccount: new FormControl<string | null>(bankSupplierAccount, [Validators.maxLength(bankSupplierAccountMaxLength)]),
      bankSupplierAccountLabel: new FormControl<string | null>(bankSupplierAccountLabel, [Validators.maxLength(bankSupplierAccountLabelMaxLength)]),
      bankSupplierAuxiliaryAccount: new FormControl<string | null>(bankSupplierAuxiliaryAccount, [Validators.maxLength(bankSupplierAuxiliaryAccountMaxLength)]),
      bankSupplierAuxiliaryAccountLabel: new FormControl<string | null>(bankSupplierAuxiliaryAccountLabel, [Validators.maxLength(bankSupplierAuxiliaryAccountLabelMaxLength)])
    });
    if ((this.fieldsConfig?.columns?.name?.canEdit != null && !this.fieldsConfig?.columns?.name?.canEdit) || isDefault) {
      result.get('name')?.disable();
    }
    if ((this.fieldsConfig?.columns?.export?.canEdit != null && !this.fieldsConfig?.columns?.export?.canEdit)) {
      result.get('export')?.disable();
    }
    if (this.fieldsConfig?.columns?.journalCode?.canEdit != null && !this.fieldsConfig?.columns?.journalCode?.canEdit) {
      result.get('journalCode')?.disable();
    }
    if (this.fieldsConfig?.columns?.internalTransfer?.canEdit != null && !this.fieldsConfig?.columns?.internalTransfer?.canEdit) {
      result.get('internalTransferAccount')?.disable();
      result.get('internalTransferAccountLabel')?.disable();
    }
    if (this.fieldsConfig?.columns?.provider?.canEdit != null && !this.fieldsConfig?.columns?.provider?.canEdit) {
      result.get('providerAccount')?.disable();
      result.get('providerAccountLabel')?.disable();
    }
    if (this.fieldsConfig?.columns?.bankSupplier?.canEdit != null && !this.fieldsConfig?.columns?.bankSupplier?.canEdit) {
      result.get('bankSupplierAccount')?.disable();
      result.get('bankSupplierAccountLabel')?.disable();
      result.get('bankSupplierAuxiliaryAccount')?.disable();
      result.get('bankSupplierAuxiliaryAccountLabel')?.disable();
    }
    this.internalTransferSubscriptions.push(
      result
      .controls['name']
      .valueChanges
      .subscribe(
        (value) => this.filteredOptions$.next(this._filter(value ?? ''))
      )
    );
    return result;
  }

  private _filter(value: string): string[] {
    const filterValue = value.toLowerCase();
    return this.paymentOptions.filter(option => option.toLowerCase().includes(filterValue));
  }

  updateLabelsForMethods(): void {
    if (this.fieldsConfig?.unique === false) {
      const internalTransferAccountLabelMaxLength = this.fieldsConfig?.columns?.internalTransfer?.label?.maxLength != null ? this.fieldsConfig?.columns?.internalTransfer?.label?.maxLength : defaultAccountLabelMaxLength;
      const providerAccountLabelMaxLength = this.fieldsConfig?.columns?.provider?.label?.maxLength != null ? this.fieldsConfig?.columns?.provider?.label?.maxLength : defaultAccountLabelMaxLength;
      const providerAccountMap: { [key: string]: string[] } = {};
      const internalTransferAccountMap: { [key: string]: string[] } = {};

      // First loop to add unique names to the map (account : array of names)
      this.formArray.controls.forEach((control) => {
        const name = control.get('name')?.value;
        const providerAccount = control.get('providerAccount')?.value;
        const internalTransferAccount = control.get('internalTransferAccount')?.value;

        // Provider
        if (providerAccount) {
          if (!providerAccountMap[providerAccount]) {
            providerAccountMap[providerAccount] = [];
          }
          if (name && !providerAccountMap[providerAccount].includes(name)) {
            providerAccountMap[providerAccount].push(name);
          }
        }

        // InternalTransfer
        if (internalTransferAccount) {
          if (!internalTransferAccountMap[internalTransferAccount]) {
            internalTransferAccountMap[internalTransferAccount] = [];
          }
          if (name && !internalTransferAccountMap[internalTransferAccount].includes(name)) {
            internalTransferAccountMap[internalTransferAccount].push(name);
          }
        }
      });

      // Second loop : compute and set labels for each controls
      this.formArray.controls.forEach((control) => {
        const providerAccount = control.get('providerAccount')?.value;
        const internalTransferAccount = control.get('internalTransferAccount')?.value;

        // Provider
        if (providerAccount) {
          let providerAccountLabel = null;
          if (providerAccountMap[providerAccount].length > 0) {
            providerAccountLabel = providerAccountMap[providerAccount].join(' / ');
          }
          control.get('providerAccountLabel')?.setValue(providerAccountLabel != null ? providerAccountLabel.slice(0, providerAccountLabelMaxLength) : null, { emitEvent: false }); // do not call updateLabelsForMethods
        }

        // InternalTransfer
        if (internalTransferAccount) {
          let internalTransferAccountLabel = null;
          if (internalTransferAccountMap[internalTransferAccount].length > 0) {
            internalTransferAccountLabel = `Virement interne ${internalTransferAccountMap[internalTransferAccount].join(' / ')}`;
          }
          control.get('internalTransferAccountLabel')?.setValue(internalTransferAccountLabel != null ? internalTransferAccountLabel.slice(0, internalTransferAccountLabelMaxLength) : null, { emitEvent: false }); // do not call updateLabelsForMethods
        }
      });
    }

    // recompute validity
    this.formArray.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Update the filtered options when focusing the input name.
   */
  updateFilteredOptionsOnFocus(formArrayIndex: number) {
    const value = this.formArray.at(formArrayIndex)?.controls['name'].value;
    this.filteredOptions$.next(this._filter(value ?? ''));
  }

  applyFetchedFieldsConfiguration(): void {
    if (this.fetchedFieldsConfiguration) {

      this.fetchedFieldsConfiguration.forEach(field => {
        const group = this.createPaymentMethodConfigurationGroup(
          null,                     // id
          field.name,               // name
          true,                     // exportValue
          field.journalCode,        // journalCode
          null,                     // internalTransferAccount
          null,                     // internalTransferAccountLabel
          field.providerAccount,   // providerAccount
          null,                     // providerAccountLabel
          null,                     // bankSupplierAccount
          null,                     // bankSupplierAccountLabel
          null,                     // bankSupplierAuxiliaryAccount
          null                      // bankSupplierAuxiliaryAccountLabel
        );

        // Insert before the default method if present
        this.formArray.insert(this.formArray.length - 1, group);
      });
      }
    }
}
